import React, { ReactElement, forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { AccordionContext } from './accordion-context';

interface AccordionProps {
  className?: string;
  defaultExpanded?: boolean;
  expanded?: boolean;
  onChange?: (e: any, expanded: boolean) => void;
  type?: 'default' | 'gray';
  blue?: boolean;
  expandedIcon?: ReactElement;
  unExpandedIcon?: ReactElement;
  children?: ReactElement[];
  disabled?: boolean;
  showExpandIcon?: boolean;
  checkIfExpanded?: (expand: boolean) => void;
}

const Accordion = forwardRef<HTMLDivElement, AccordionProps>(function Accordion(
  {
    className,
    children: childrenProp,
    expanded: expandedProp,
    defaultExpanded,
    disabled,
    onChange,
    type,
    blue,
    expandedIcon,
    unExpandedIcon,
    showExpandIcon = true,
    checkIfExpanded,
  },
  ref
) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [summary, ...otherChildren] = React.Children.toArray(childrenProp);
  const { current: isControlled } = useRef(expandedProp !== undefined);
  useEffect(() => {
    checkIfExpanded && checkIfExpanded(expanded);
  }, [expanded]);
  const setExpandedState = useCallback((val) => {
    if (isControlled) return;

    setExpanded(val);
  }, []);

  const handleChange = useCallback(
    (event) => {
      setExpandedState(!expanded);

      onChange && onChange(event, !expanded);
    },
    [expanded, onChange, setExpandedState]
  );

  const contextValue = useMemo(
    () => ({ expanded, disabled, toggle: handleChange, type, expandedIcon, unExpandedIcon, showExpandIcon }),
    [expanded, disabled, handleChange, type, expandedIcon, unExpandedIcon, showExpandIcon]
  );

  useEffect(() => {
    if (expandedProp === undefined) return;
    setExpanded(expandedProp);
  }, [expandedProp]);

  return (
    <div
      className={clsx(
        'c-accordion c-accordion__item',
        type === 'gray' && 'c-accordion__item--resetMarginTop c-accordion__item--resetMinHeight',
        blue && 'c-accordion--blue',
        className && className
      )}
      ref={ref}
    >
      <AccordionContext.Provider value={contextValue}>
        {summary}
        {expanded && <>{otherChildren}</>}
      </AccordionContext.Provider>
    </div>
  );
});

export { Accordion };
